import React, {useEffect, useState} from "react"
import {useParams} from "react-router-dom"
import {useModal} from "react-modal-hook"
import {useToasts} from "react-toast-notifications"
import TitleBar, {MetaItemProps, TitleBarAction} from "../../components/TitleBar/TitleBar"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {MainSection} from "../../containers/MainSection/MainSection"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useAccessToken, useIsOrgOrUnitAdminUser, useIsUnitTypeUser, useIsUnitUser} from "../../services/auth"
import {hasPermission} from "../../services/permission"
import {ActionButton, ActionItem, ActionItemProps} from "../../components/ActionButton/ActionButton"
import Drawer from "../../components/Drawer/Drawer"
import CardLimits from "../../components/Limits/CardLimits/CardLimits"
import {FreezeCardModal} from "../../components/AccountCards/FreezeCardModal"
import {ReplaceCardModal} from "../../components/AccountCards/ReplaceCardModal"
import {ReactivateCardModal} from "../../components/AccountCards/ReactivateCardModal"
import {ConfirmationModal} from "../../components/ConfirmationModal/ConfirmationModal"
import {CustomerOutreachModal} from "../../components/AccountCards/CustomerOutreachModal/CustomerOutreachModal"
import {
	Card,
	getCard,
	canCloseCard,
	canResetPin,
	closeCard,
	isReactivable,
	isReplaceable,
	resetCardPin,
	unfreezeCard,
	BusinessDebitCard,
	IndividualDebitCard,
	PhysicalCard,
	isBusinessCard,
	isCreditCard,
} from "../../resources/card"
import {BusinessCustomer, getCustomerName, IndividualCustomer} from "../../resources/customer"
import {DepositAccount} from "../../resources/account"
import {Resource} from "../../resources/common"
import {IcomoonIconName} from "../../components/Icon/icons"
import {Transactions} from "../../components/Transactions/Transactions"
import {TransactionsColumns} from "../../components/Transactions/Transactions.types"
import {
	AccountAuthorizations,
	AccountAuthorizationsColumns,
} from "../../components/AccountAuthorizations/AccountAuthorizations"
import Icon from "../../components/Icon/Icon"
import CardInfo from "./CardInfo"
import {AuditLogDrawer} from "../../components/AuditLogDrawer/AuditLogDrawer"
import {useAuditLog} from "../../components/AuditLogDrawer/useAuditLog"
import {AuditLogActionButton} from "../../components/AuditLogActionButton/AuditLogActionButton"
import {Org, isOrg} from "../../resources/org"
import {useListReturnPath} from "../../hooks/useListReturnPath"
import {AuditLogEventsHeaderMetaItemProps} from "../../components/AuditLogEventsHeader/AuditLogEventsHeader"
import {CardStatusComponent} from "../../components/Cards/Cards"

type CardPageInternalProps = {
	card: Card
	customer?: IndividualCustomer | BusinessCustomer
	account?: DepositAccount
	org?: Org
	accessToken: string
	refresh: () => void
}

function CardPageInternal({card, customer, account, accessToken, refresh}: CardPageInternalProps) {
	const listReturnPath = useListReturnPath("/cards/")
	const {addToast} = useToasts()
	const isUnitUser = useIsUnitUser()
	const isOrgOrUnitAdminUser = useIsOrgOrUnitAdminUser()
	const isUnitTypeUser = useIsUnitTypeUser()

	const [isLimitsDrawerOpen, setIsLimitsDrawerOpen] = useState(false)
	const [isActivityDrawerOpen, setIsActivityDrawerOpen] = useState(false)

	const [closeState, close] = useAsyncResultIdle(closeCard)
	const [resetPinState, resetPin] = useAsyncResultIdle(resetCardPin)
	const [unfreezeState, unfreeze] = useAsyncResultIdle(unfreezeCard)

	const [showFreeze, hideFreeze] = useModal(() => (
		<FreezeCardModal close={hideFreeze} card={card} onSuccess={refresh} />
	))
	const [showReplace, hideReplace] = useModal(() => (
		<ReplaceCardModal customer={customer} close={hideReplace} card={card as PhysicalCard} onSuccess={refresh} />
	))
	const [showReactivate, hideReactivate] = useModal(() => (
		<ReactivateCardModal
			customer={customer}
			close={hideReactivate}
			card={card as BusinessDebitCard | IndividualDebitCard}
			onSuccess={refresh}
		/>
	))
	const [showCloseConfirmationModal, hideCloseConfirmationModal] = useModal(
		() => (
			<ConfirmationModal
				title="Close Card"
				close={hideCloseConfirmationModal}
				okButtonText={"Close Card"}
				okButtonClassname={"button is-danger is-outlined"}
				cancelButtonClassname="button is-white"
				onSubmit={() => {
					close(accessToken, card)
				}}
			>
				Are you sure you want to close this card?
			</ConfirmationModal>
		),
		[]
	)
	const [showResetPinConfirmationModal, hideResetPinConfirmationModal] = useModal(
		() => (
			<ConfirmationModal
				title="Reset PIN"
				close={hideResetPinConfirmationModal}
				okButtonText={"Reset PIN"}
				okButtonClassname={"button is-danger is-outlined"}
				cancelButtonClassname="button is-white"
				onSubmit={() => {
					resetPin(accessToken, card)
				}}
			>
				Are you sure you want to reset the PIN for this card?
				<br />
				This is a high-risk action that should only be taken following the card holder&apos;s request, and after the
				process of customer identity verification has been completed
			</ConfirmationModal>
		),
		[]
	)
	const [showCustomerOutreach, hideCustomerOutreach] = useModal(() => (
		<CustomerOutreachModal
			close={hideCustomerOutreach}
			card={card}
			customer={customer as IndividualCustomer | BusinessCustomer}
			onSuccess={refresh}
		/>
	))

	useEffect(() => {
		closeState.match(
			() => null,
			() => null,
			() => refresh(),
			(e) => addToast(e.errors[0].title, {appearance: "error"})
		)
	}, [closeState])

	useEffect(() => {
		unfreezeState.match(
			() => null,
			() => null,
			() => refresh(),
			(e) => addToast(e.errors[0].title, {appearance: "error"})
		)
	}, [unfreezeState])

	useEffect(() => {
		resetPinState.match(
			() => null,
			() => null,
			() => {
				addToast("The PIN has been reset successfully", {appearance: "success"})
				refresh()
			},
			(e) => addToast(e.errors[0].title, {appearance: "error"})
		)
	}, [resetPinState])

	// Get customer name for title meta
	const customerName = customer ? getCustomerName(customer) : ""

	// Build title meta items - following the design: customer name link, account link
	const titleMeta: MetaItemProps[] = []

	// Customer name link
	if (customerName) {
		titleMeta.push({
			text: customerName,
			icon: "user-geometric-action-money-fund---users" as IcomoonIconName,
			path: `/customers/${card.relationships.customer.data.id}`,
		})
	}

	// Account link
	titleMeta.push({
		text: "Account",
		icon: "bank-account--business-products" as IcomoonIconName,
		path: `/accounts/${card.relationships.account.data.id}`,
	})

	// Build action items
	const actionItems: ActionItemProps[] = []

	if (card.attributes.status === "Active" || card.attributes.status === "ActiveForOnlineUse") {
		actionItems.push({
			title: "Freeze Card",
			icon: "credit-card-lock",
			onClick: () => showFreeze(),
		})
	}

	if (card.attributes.status === "Frozen") {
		actionItems.push({
			title: "Unfreeze Card",
			icon: "credit-card-unlock",
			onClick: () => unfreeze(accessToken, card),
		})
	}

	if (isReplaceable(card) && hasPermission("card.replace", "create")) {
		actionItems.push({
			title: "Replace Card",
			icon: "account-credit-card-replace",
			onClick: () => showReplace(),
		})
	}

	if (isReactivable(card) && isOrgOrUnitAdminUser) {
		actionItems.push({
			title: "Reactivate Card",
			icon: "credit-card-unlock",
			onClick: () => showReactivate(),
		})
	}

	if (canResetPin(card) && isOrgOrUnitAdminUser) {
		actionItems.push({
			title: "Reset PIN",
			icon: "security-password-lock--programing-apps-websites",
			onClick: () => showResetPinConfirmationModal(),
		})
	}

	if (hasPermission("sendLink.card.unfreeze", "create")) {
		actionItems.push({
			title: "Send Customer Outreach",
			icon: "email-share--emails",
			onClick: () => showCustomerOutreach(),
		})
	}

	if (isUnitTypeUser) {
		actionItems.push({
			title: "Card Activity",
			icon: "list-bullet-point-square",
			onClick: () => setIsActivityDrawerOpen(true),
		})
	}

	if (canCloseCard(card)) {
		actionItems.push({
			title: "Close Card",
			icon: "credit-card-remove-cross",
			color: "#FF506F",
			onClick: () => showCloseConfirmationModal(),
		})
	}

	const accountId = card.relationships.account.data.id
	const cardId = card.id

	const {showAuditLog, openAuditLogDrawer, closeAuditLogDrawer, isAuditLogDrawerOpened} = useAuditLog()

	const auditLogEventsHeaderMetaItems = [
		{
			text: `•• ${card.attributes.last4Digits}`,
			icon: "credit-card",
		},
		{
			text: account?.attributes.name,
			icon: "bank-account--business-products",
		},
	] as AuditLogEventsHeaderMetaItemProps[]

	function isInactive(card: Card) {
		const inactiveStatuses = ["Frozen", "Inactive", "Stolen", "Lost", "ClosedByCustomer", "SuspectedFraud"]
		return inactiveStatuses.includes(card.attributes.status)
	}

	function getCardUrl() {
		const cardImageMap = {
			inactive: {
				debit: "debit_card",
				business: "business_debit_card",
				business_credit: "business_credit_card_disabled",
			},
			active: {
				debit: "card_main2",
				business: "card_main",
				business_credit: "business_credit_card",
			},
		}

		const url = `https://ui.dev.unit.sh/resources/{{card}}.png`

		return url.replace(
			"{{card}}",
			cardImageMap[isInactive(card) ? "inactive" : "active"][
				getCardType(card) as keyof typeof cardImageMap[keyof typeof cardImageMap]
			]
		)
	}

	function getCardType(card: Card) {
		if (isBusinessCard(card)) {
			if (isCreditCard(card)) {
				return "business_credit"
			}
			return "business"
		}

		return "debit"
	}

	return (
		<>
			<TitleBar
				title={`•• ${card.attributes.last4Digits}`}
				breadcrumbs={[{text: "Cards", path: listReturnPath}]}
				meta={titleMeta}
				status={<CardStatusComponent card={card} className="badge-left-space" />}
			>
				{showAuditLog && <AuditLogActionButton onClick={openAuditLogDrawer} />}
				<a
					className="card-limits-link"
					href="#"
					onClick={(e) => {
						e.preventDefault()
						setIsLimitsDrawerOpen(true)
					}}
				>
					<Icon icon={"icon-settings-slider-horizontal"} size={16} />
					<span>Card limits</span>
				</a>
				<TitleBarAction>
					{actionItems.length > 0 && (
						<ActionButton enableActions={true}>
							{actionItems.map((item, index) => (
								<ActionItem key={`${item.title}-${index}`} {...item} />
							))}
						</ActionButton>
					)}
				</TitleBarAction>
			</TitleBar>

			<MainSection>
				<AuditLogDrawer
					open={isAuditLogDrawerOpened}
					onClose={closeAuditLogDrawer}
					auditLogEventsHeaderMetaItems={auditLogEventsHeaderMetaItems}
					resourceType="card"
					resourceId={card.id}
				/>

				{/* Card Limits Drawer */}
				<Drawer
					open={isLimitsDrawerOpen}
					onClose={() => setIsLimitsDrawerOpen(false)}
					direction="right"
					className="account-side-drawer"
					title={`Card-Level Limits ••${card.attributes.last4Digits}`}
				>
					{isLimitsDrawerOpen && <CardLimits accountId={accountId} card={card} accessToken={accessToken} />}
				</Drawer>

				{/* Card Activity Drawer */}
				<Drawer
					open={isActivityDrawerOpen}
					onClose={() => setIsActivityDrawerOpen(false)}
					direction="right"
					className="account-side-drawer"
					title={`Card Activity ••${card.attributes.last4Digits}`}
				>
					{isActivityDrawerOpen && (
						<div className="p-4">
							<unit-elements-activity
								hide-filter-button={"true"}
								hide-title={"true"}
								pagination-type={"infinite-scroll"}
								customer-token={accessToken}
								query-filter={`filter[cardId]=${card.id}&filter[type][0]=Card`}
							/>
						</div>
					)}
				</Drawer>

				<div className="columns">
					{/* Left Column - Card Visual and Information */}
					<div className="card-page-left-column">
						{/* Unit Elements Card Component */}
						<div className="card card-page-card-element-wrapper">
							<div className="card-content">
								<unit-elements-card
									theme="light"
									settings-json={`{"global": {"colors": {
      "error": "#ff506f",
      "warning": "#ff9d42",
      "success": "#44d47f"
    },"typography": {"common": {"rootFontSize": "14"}}},"elementsCard":{"designs":[{"name":"default", "fontColor": "#545558","url":"${getCardUrl()}", "boxShadow":"none"},{"name":"default_credit", "fontColor": "#545558","url":"${getCardUrl()}", "boxShadow":"none"}] }}`}
									card-id={card.id}
									customer-token={accessToken}
									hide-card-title={"true"}
									hide-actions-menu-button={"true"}
									hide-sensitive-data-button={"true"}
									hide-activate-button={"true"}
									hide-sensitive-data={"true"}
									card-holder-name-style={"capitalize"}
									show-status-badge={"true"}
									hide-inactive-blur={"true"}
									language={"https://ui.s.unit.sh/resources/982/languages/91350ee4-28c4-454a-aafe-915a788c6b13.json"}
								/>
							</div>
							<CardInfo card={card} account={account} />
						</div>
					</div>

					{/* Right Column - Tables */}
					<div className="column card-page-right-column">
						{/* Transactions Table */}
						<Transactions
							fullHeight={false}
							accountId={accountId}
							cardId={cardId}
							limit={10}
							enableSearch={true}
							token={accessToken}
							includedColumns={[
								TransactionsColumns.id,
								TransactionsColumns.type,
								TransactionsColumns.customer,
								TransactionsColumns.balance,
								TransactionsColumns.summary,
								TransactionsColumns.amount,
								TransactionsColumns.createdAt,
							]}
						/>

						{/* Authorizations Table */}
						{hasPermission("authorization", "get") && (
							<AccountAuthorizations
								accountId={accountId}
								cardId={cardId}
								limit={10}
								token={accessToken}
								refreshPage={refresh}
								includedColumns={[
									AccountAuthorizationsColumns.id,
									AccountAuthorizationsColumns.last4Digits,
									AccountAuthorizationsColumns.merchantName,
									AccountAuthorizationsColumns.status,
									AccountAuthorizationsColumns.reason,
									AccountAuthorizationsColumns.declinedBy,
									AccountAuthorizationsColumns.createdAt,
									AccountAuthorizationsColumns.amount,
									...(isUnitUser ? [AccountAuthorizationsColumns.actions] : []),
								]}
							/>
						)}
					</div>
				</div>
			</MainSection>
		</>
	)
}

export default function CardPage() {
	const {cardId = ""} = useParams<{cardId: string}>()
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const asyncResult = useAsyncResult(() => getCard(accessToken, cardId), [refreshToken, cardId])

	return (
		<AsyncResultComponent asyncResult={asyncResult}>
			{({value}) => {
				const card = value.data
				const included = value.included as Resource[] | undefined

				// Find customer from included
				const customer = included?.find(
					(r) =>
						(r.type === "individualCustomer" || r.type === "businessCustomer") &&
						r.id === card.relationships.customer.data.id
				) as IndividualCustomer | BusinessCustomer | undefined

				// Find account from included
				const account = included?.find(
					(r) => r.type?.toString().toLowerCase().includes("account") && r.id === card.relationships.account.data.id
				) as DepositAccount | undefined

				// Find org from included
				const org = account?.relationships?.org?.data?.id
					? (included?.find((r) => isOrg(r) && r.id === account.relationships.org.data.id) as Org | undefined)
					: undefined

				return (
					<CardPageInternal
						card={card}
						customer={customer}
						account={account}
						org={org}
						accessToken={accessToken}
						refresh={refresh}
					/>
				)
			}}
		</AsyncResultComponent>
	)
}
